"use client";

import { useMemo } from "react";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { NoxusUIMessage } from "../noxus-message-types";
import type { PendingApproval } from "./approvalGate";
import {
  ALWAYS_ALLOW_REASON,
  DEFAULT_DENY_REASON,
  getPendingApproval,
} from "./approvalGate";

export interface ActiveApproval {
  pending: PendingApproval;
  approve: () => void;
  alwaysAllow: () => void;
  // The message, when given, reaches the agent as the denial reason.
  deny: (message?: string) => void;
}

type ChatHandle = Pick<
  UseChatHelpers<NoxusUIMessage>,
  "messages" | "addToolApprovalResponse"
>;

// ``onAnswer`` runs after each answer (the chat scrolls to the turn it
// resumes).
export function useApproval(
  chat: ChatHandle,
  onAnswer?: () => void
): ActiveApproval | null {
  const { messages, addToolApprovalResponse } = chat;
  const pending = useMemo(() => getPendingApproval(messages), [messages]);
  const approvalId = pending?.approvalId;

  const actions = useMemo(() => {
    if (!approvalId) return null;
    const answer = (response: { approved: boolean; reason?: string }): void => {
      addToolApprovalResponse({ id: approvalId, ...response });
      onAnswer?.();
    };
    return {
      approve: () => answer({ approved: true }),
      alwaysAllow: () =>
        answer({ approved: true, reason: ALWAYS_ALLOW_REASON }),
      deny: (message?: string) =>
        answer({
          approved: false,
          reason: message?.trim() || DEFAULT_DENY_REASON,
        }),
    };
  }, [approvalId, addToolApprovalResponse, onAnswer]);

  return pending && actions ? { pending, ...actions } : null;
}
